import React from 'react';
import { Card } from '../components/ui/Card';
import { formatCurrency } from '../utils/calculations';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
export function JobProfit() {
  const jobData = [{
    id: 'job-1',
    name: 'GarrettAuto.com Website',
    customer: 'Garrett Auto',
    revenue: 1500,
    costs: {
      labor: 800,
      materials: 200,
      overhead: 150
    },
    status: 'In Progress'
  }, {
    id: 'job-2',
    name: 'SamsDisposal.com Redesign',
    customer: 'Sams Disposal',
    revenue: 5000,
    costs: {
      labor: 2200,
      materials: 500,
      overhead: 400
    },
    status: 'In Progress'
  }, {
    id: 'job-3',
    name: 'DesertSageSeminars.com',
    customer: 'Desert Sage Seminars',
    revenue: 8200,
    costs: {
      labor: 3500,
      materials: 800,
      overhead: 600
    },
    status: 'In Progress'
  }, {
    id: 'job-4',
    name: 'Echo AWS Infrastructure',
    customer: 'Echo',
    revenue: 50000,
    costs: {
      labor: 18000,
      materials: 12000,
      overhead: 3000
    },
    status: 'In Progress'
  }, {
    id: 'job-5',
    name: 'LuckyDuckBath.com',
    customer: 'Lucky Duck Bath',
    revenue: 2500,
    costs: {
      labor: 1100,
      materials: 300,
      overhead: 200
    },
    status: 'In Progress'
  }, {
    id: 'job-6',
    name: 'TeamNTA.com',
    customer: 'Team NTA',
    revenue: 2750,
    costs: {
      labor: 1200,
      materials: 350,
      overhead: 220
    },
    status: 'In Progress'
  }];
  const jobsWithProfit = jobData.map(job => {
    const totalCosts = job.costs.labor + job.costs.materials + job.costs.overhead;
    const profit = job.revenue - totalCosts;
    const margin = profit / job.revenue * 100;
    return {
      ...job,
      totalCosts,
      profit,
      margin
    };
  });
  const totalRevenue = jobsWithProfit.reduce((sum, job) => sum + job.revenue, 0);
  const totalCosts = jobsWithProfit.reduce((sum, job) => sum + job.totalCosts, 0);
  const totalProfit = totalRevenue - totalCosts;
  const averageMargin = totalProfit / totalRevenue * 100;
  const mostProfitableJob = jobsWithProfit.reduce((max, job) => job.profit > max.profit ? job : max);
  const highestMarginJob = jobsWithProfit.reduce((max, job) => job.margin > max.margin ? job : max);
  return <div className="space-y-6" data-id="element-1662">
      <div data-id="element-1663">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1664">Job Profit</h1>
        <p className="text-sm text-gray-600" data-id="element-1665">
          Track profitability by job or project
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1666">
        <Card data-id="element-1667">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1668">Total Revenue</p>
          <p className="text-2xl font-bold text-blue-600" data-id="element-1669">
            {formatCurrency(totalRevenue)}
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-1670">
            {jobsWithProfit.length} active jobs
          </p>
        </Card>

        <Card data-id="element-1671">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1672">Total Costs</p>
          <p className="text-2xl font-bold text-red-600" data-id="element-1673">
            {formatCurrency(totalCosts)}
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-1674">All jobs combined</p>
        </Card>

        <Card data-id="element-1675">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1676">Total Profit</p>
          <p className="text-2xl font-bold text-green-600" data-id="element-1677">
            {formatCurrency(totalProfit)}
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-1678">
            {averageMargin.toFixed(1)}% avg margin
          </p>
        </Card>

        <Card data-id="element-1679">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1680">Best Margin</p>
          <p className="text-2xl font-bold text-purple-600" data-id="element-1681">
            {highestMarginJob.margin.toFixed(1)}%
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-1682">{highestMarginJob.name}</p>
        </Card>
      </div>

      {/* Jobs Table */}
      <Card data-id="element-1683">
        <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1684">
          Job Profitability
        </h2>
        <div className="overflow-x-auto" data-id="element-1685">
          <table className="w-full" data-id="element-1686">
            <thead data-id="element-1687">
              <tr className="border-b-2 border-gray-200" data-id="element-1688">
                <th className="text-left py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1689">
                  Job Name
                </th>
                <th className="text-left py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1690">
                  Customer
                </th>
                <th className="text-right py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1691">
                  Revenue
                </th>
                <th className="text-right py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1692">
                  Costs
                </th>
                <th className="text-right py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1693">
                  Profit
                </th>
                <th className="text-right py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1694">
                  Margin
                </th>
                <th className="text-center py-3 px-2 text-sm font-semibold text-gray-700" data-id="element-1695">
                  Status
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1696">
              {jobsWithProfit.map(job => <tr key={job.id} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1697">
                  <td className="py-3 px-2 text-sm font-medium text-gray-900" data-id="element-1698">
                    {job.name}
                  </td>
                  <td className="py-3 px-2 text-sm text-gray-600" data-id="element-1699">
                    {job.customer}
                  </td>
                  <td className="py-3 px-2 text-sm text-right font-medium text-gray-900" data-id="element-1700">
                    {formatCurrency(job.revenue)}
                  </td>
                  <td className="py-3 px-2 text-sm text-right text-red-600" data-id="element-1701">
                    {formatCurrency(job.totalCosts)}
                  </td>
                  <td className="py-3 px-2 text-sm text-right font-semibold text-green-600" data-id="element-1702">
                    {formatCurrency(job.profit)}
                  </td>
                  <td className="py-3 px-2 text-sm text-right" data-id="element-1703">
                    <span className={`inline-flex items-center gap-1 ${job.margin >= 50 ? 'text-green-600' : job.margin >= 30 ? 'text-blue-600' : 'text-orange-600'}`} data-id="element-1704">
                      {job.margin >= 50 ? <TrendingUpIcon className="w-3 h-3" data-id="element-1705" /> : job.margin < 30 ? <TrendingDownIcon className="w-3 h-3" data-id="element-1706" /> : null}
                      <span className="font-semibold" data-id="element-1707">
                        {job.margin.toFixed(1)}%
                      </span>
                    </span>
                  </td>
                  <td className="py-3 px-2 text-center" data-id="element-1708">
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded" data-id="element-1709">
                      {job.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
            <tfoot data-id="element-1710">
              <tr className="border-t-2 border-gray-300 bg-gray-50" data-id="element-1711">
                <td className="py-3 px-2 text-sm font-bold text-gray-900" colSpan={2} data-id="element-1712">
                  Totals
                </td>
                <td className="py-3 px-2 text-sm text-right font-bold text-blue-600" data-id="element-1713">
                  {formatCurrency(totalRevenue)}
                </td>
                <td className="py-3 px-2 text-sm text-right font-bold text-red-600" data-id="element-1714">
                  {formatCurrency(totalCosts)}
                </td>
                <td className="py-3 px-2 text-sm text-right font-bold text-green-600" data-id="element-1715">
                  {formatCurrency(totalProfit)}
                </td>
                <td className="py-3 px-2 text-sm text-right font-bold text-gray-900" data-id="element-1716">
                  {averageMargin.toFixed(1)}%
                </td>
                <td data-id="element-1717"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {/* Cost Breakdown for Top Job */}
      <Card data-id="element-1718">
        <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1719">
          Cost Breakdown: {mostProfitableJob.name}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1720">
          <div className="p-4 bg-blue-50 rounded-lg" data-id="element-1721">
            <p className="text-sm text-gray-600 mb-1" data-id="element-1722">Labor Costs</p>
            <p className="text-xl font-bold text-blue-600" data-id="element-1723">
              {formatCurrency(mostProfitableJob.costs.labor)}
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-1724">
              {(mostProfitableJob.costs.labor / mostProfitableJob.totalCosts * 100).toFixed(0)}
              % of total costs
            </p>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg" data-id="element-1725">
            <p className="text-sm text-gray-600 mb-1" data-id="element-1726">Materials</p>
            <p className="text-xl font-bold text-orange-600" data-id="element-1727">
              {formatCurrency(mostProfitableJob.costs.materials)}
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-1728">
              {(mostProfitableJob.costs.materials / mostProfitableJob.totalCosts * 100).toFixed(0)}
              % of total costs
            </p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg" data-id="element-1729">
            <p className="text-sm text-gray-600 mb-1" data-id="element-1730">Overhead</p>
            <p className="text-xl font-bold text-purple-600" data-id="element-1731">
              {formatCurrency(mostProfitableJob.costs.overhead)}
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-1732">
              {(mostProfitableJob.costs.overhead / mostProfitableJob.totalCosts * 100).toFixed(0)}
              % of total costs
            </p>
          </div>
        </div>
      </Card>
    </div>;
}